import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { CalendarIcon } from "lucide-react";
import moment from "moment";
import { useState, useEffect, useRef } from "react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "components/ui/select";

export default function DatePickerInput({
  value,
  className,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
}: {
  value: string;
  className?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const initialDate = value ? moment(value, "YYYY-MM-DD").toDate() : undefined;
  const [date, setDate] = useState<Date | undefined>(initialDate);
  const [inputValue, setInputValue] = useState(value ? moment(initialDate).format("DD-MMM-YYYY") : "");
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [selectedYear, setSelectedYear] = useState<any>(date ? moment(date).year() : moment().year());
  const [selectedMonth, setSelectedMonth] = useState<any>(date ? moment(date).month() : moment().month());
  const calendarRef = useRef<HTMLDivElement>(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 55 }, (_, i) => currentYear + 5 - i);
  const months = moment.months();

  useEffect(() => {
    if (value) {
      const parsed = moment(value, "YYYY-MM-DD");
      if (parsed.isValid()) {
        const parsedDate = parsed.toDate();
        setDate(parsedDate);
        setInputValue(parsed.format("DD-MMM-YYYY"));
        setSelectedYear(parsed.year());
        setSelectedMonth(parsed.month());
      }
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val); // always reflect user input
    console.log(val, "data");

    if (!val) {
      // if input is cleared
      setDate(undefined);
      setSelectedYear(undefined); // ✅ clear it too
      setSelectedMonth(undefined); // ✅ clear it too
      onChange(""); // only if onChange accepts null
      return;
    }

    const formats = ["YYYY-MM-DD", "YYYY-M-D", "D-M-YYYY", "DD-MM-YYYY", "M-D-YYYY", "MM-DD-YYYY"];
    const parsed = moment(val, formats, true); // strict match

    if (parsed.isValid()) {
      const selected = parsed.toDate();
      setDate(selected);
      setSelectedYear(parsed.year());
      setSelectedMonth(parsed.month());
      onChange(parsed.format("YYYY-MM-DD"));
      console.log("values", parsed.format("YYYY-MM-DD"));
      setInputValue(parsed.format("DD-MMM-YYYY"));
    } else {
      // optionally clear values or show error
    }
  };

  const handleYearChange = (year: string) => {
    const newYear = Number(year);
    setSelectedYear(newYear);
  };

  const handleMonthChange = (month: string) => {
    const newMonth = moment().month(month).month();
    setSelectedMonth(newMonth);
  };

  return (
    <div className="flex justify-center items-center m-0 p-0">
      <input
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className="w-[180px] text-[15px] outline-none py-4 px-5 h-[45px] border-[1px]  border-r-0 rounded-tl-md rounded-bl-md bg-transparent"
        spellCheck={false}
        data-datepicker-toggle
        disabled={disabled}
      />
      <Popover open={isOpenDialog} onOpenChange={setIsOpenDialog}>
        <PopoverTrigger asChild>
          <button className=" bg-transparent text-black hover:bg-none hover:bg-transparent border-[1px] pe-3 h-[45px] border-l-0 rounded-tr-md rounded-br-md">
            <CalendarIcon className="ml-auto h-4 w-4 opacity-50 dark:text-white" />
          </button>
        </PopoverTrigger>

        <PopoverContent ref={calendarRef} className="w-auto p-0" align="start">
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2 pt-2 px-2">
              <Select value={months[selectedMonth]} onValueChange={handleMonthChange}>
                <SelectTrigger>
                  <SelectValue>{months[selectedMonth]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedYear?.toString()} onValueChange={handleYearChange}>
                <SelectTrigger>
                  <SelectValue>{selectedYear}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year?.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Calendar
              className="p-0 px-2 pb-2"
              mode="single"
              selected={date}
              month={selectedYear !== undefined && selectedMonth !== undefined ? moment({ year: selectedYear, month: selectedMonth }).toDate() : new Date()}
              onMonthChange={(newMonth) => {
                setSelectedYear(moment(newMonth).year());
                setSelectedMonth(moment(newMonth).month());
              }}
              onSelect={(selectedDate) => {
                if (selectedDate) {
                  const formatted = moment(selectedDate).format("YYYY-MM-DD");
                  setDate(selectedDate);
                  setInputValue(moment(selectedDate).format("DD-MMM-YYYY"));
                  onChange(formatted);
                  setIsOpenDialog(false);
                }
              }}
              disabled={disabled}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
